import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { hasNativeCapability, openNativeScanner } from '../../utils/nativeShell';
import { QRCodeSVG } from 'qrcode.react';
import { ecosystemRequest } from '../../services/api/passportEcosystem';
import { useAuth } from '../../contexts/AuthContext';
type BoundRequest = {
  purpose: string;
  collector: string;
  subject: string | null;
  benefit: string | null;
  evidence: string | null;
  session_ref: string | null;
  state: string;
  expires_at: string;
};
type Assignment = {
  assignment_ref: string;
  title: string;
  version_ref: string;
  organization: string;
  status: string;
  actions_json: string;
  subject_ref: number | null;
  subject_public: string | null;
  instructions: string;
  timezone: string;
  stops: Array<{ subject_ref: string; title: string; paused: boolean }>;
  sessions: Array<{
    public_id: string;
    title: string;
    starts_at: string;
    ends_at: string;
    timezone: string;
    status: string;
  }>;
  expires_at: string;
};
export default function PassportOperationsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]),
    [message, setMessage] = useState(''),
    [challenge, setChallenge] = useState(''),
    [reason, setReason] = useState(''),
    [busy, setBusy] = useState(false),
    [camera, setCamera] = useState(false),
    [print, setPrint] = useState<{ url: string; label: string } | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const command = useRef('');
  const [bound, setBound] = useState<BoundRequest | null>(null);

  useEffect(() => {
    let active = true;
    setBound(null);
    if (challenge)
      void ecosystemRequest<BoundRequest>('inspect', { challenge }, {}, 'operations')
        .then((r) => {
          if (active) setBound(r);
        })
        .catch((e) => {
          if (active) setMessage(e.message);
        });
    return () => {
      active = false;
    };
  }, [challenge, user?.id]);
  useEffect(() => {
    let active = true;
    setAssignments([]);
    setBound(null);
    setPrint(null);
    setReason('');
    setMessage('');
    command.current = '';
    setChallenge('');
    setCamera(false);
    void ecosystemRequest<Assignment[]>('assigned', undefined, {}, 'operations')
      .then((rows) => {
        if (active) setAssignments(rows);
      })
      .catch((e) => {
        if (active) setMessage(e.message);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);
  useEffect(() => {
    if (!user?.id) return;
    const raw = new URLSearchParams(location.hash.replace(/^#/, '')).get('request');
    if (!raw) return;
    navigate(location.pathname + location.search, { replace: true });
    if (/^bilt:(visit|redemption):[a-f0-9]{48}$/.test(raw)) {
      setChallenge(raw);
      command.current = crypto.randomUUID();
    }
  }, [location.hash, location.pathname, location.search, user?.id, navigate]);
  useEffect(() => {
    if (!camera) return;
    let active = true;
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout>;
    void (async () => {
      try {
        const { BarcodeDetector, setZXingModuleOverrides } =
          await import('barcode-detector/ponyfill');
        setZXingModuleOverrides({ locateFile: () => '/scanner/zxing_reader.wasm' });
        const detector = new BarcodeDetector({ formats: ['qr_code'] });
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play();
        }
        const tick = async () => {
          if (!active) return;
          try {
            if (video.current?.readyState === 4) {
              const codes = await detector.detect(video.current);
              const code = codes.find((c) =>
                /^bilt:(visit|redemption):[a-f0-9]{48}$/.test(c.rawValue)
              );
              if (code) {
                setChallenge(code.rawValue);
                command.current = crypto.randomUUID();
                setCamera(false);
                return;
              }
            }
          } catch {}
          timer = setTimeout(() => void tick(), 250);
        };
        void tick();
      } catch (e) {
        if (active) {
          setMessage((e as Error).message);
          setCamera(false);
        }
      }
    })();
    return () => {
      active = false;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [camera]);
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await ecosystemRequest<{
        confirmed?: boolean;
        redeemed?: boolean;
        receipt_ref?: string;
        visit_ref?: string;
      }>('confirm', { command_id: command.current, challenge, reason }, {}, 'operations');
      setMessage(
        r.redeemed
          ? `Redeemed. Receipt ${r.receipt_ref}`
          : `Visit confirmed. Receipt ${r.visit_ref}`
      );
      setChallenge('');
      setReason('');
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="pc-page">
      <Link className="pc-link" to="/passport">
        Back to Passport
      </Link>
      <h1>Program operations</h1>
      <Link className="pc-link" to="/passport/places">
        Manage your places and QR codes
      </Link>
      <p>Operate only the published programs assigned to your account.</p>
      <div className="pe-program-grid">
        {assignments.map((a) => (
          <article key={a.assignment_ref}>
            <span className="pe-kicker">
              {a.organization} · {a.status}
            </span>
            <h2>{a.title}</h2>
            <p>{a.instructions}</p>
            <p>
              Assignment expires {new Date(a.expires_at.replace(' ', 'T') + 'Z').toLocaleString()}
            </p>
            {JSON.parse(a.actions_json).includes('pause_stop') &&
              a.stops.map((stop) => (
                <div key={stop.subject_ref}>
                  <p>
                    {stop.title} · {stop.paused ? 'Paused' : 'Open'}
                  </p>
                  <label>
                    Operating reason
                    <input value={reason} onChange={(e) => setReason(e.target.value)} />
                  </label>
                  <button
                    className="pc-button"
                    disabled={busy || reason.length < 5}
                    onClick={() => {
                      setBusy(true);
                      void ecosystemRequest(
                        'stop',
                        {
                          version_ref: a.version_ref,
                          subject_ref: stop.subject_ref,
                          status: stop.paused ? 'open' : 'paused',
                          reason,
                        },
                        {},
                        'operations'
                      )
                        .then(() => {
                          setAssignments((rows) =>
                            rows.map((row) =>
                              row.assignment_ref === a.assignment_ref
                                ? {
                                    ...row,
                                    stops: row.stops.map((s) =>
                                      s.subject_ref === stop.subject_ref
                                        ? { ...s, paused: !s.paused }
                                        : s
                                    ),
                                  }
                                : row
                            )
                          );
                          setMessage(
                            stop.paused
                              ? 'Stop resumed. Visits during its pause remain ineligible.'
                              : 'Stop paused. Existing collections remain available.'
                          );
                        })
                        .catch((e) => setMessage(e.message))
                        .finally(() => setBusy(false));
                    }}
                  >
                    {stop.paused ? 'Resume stop' : 'Pause stop'}
                  </button>
                </div>
              ))}
            {JSON.parse(a.actions_json).includes('print') && (
              <button
                className="pc-button"
                onClick={() =>
                  void ecosystemRequest<{ url: string; label: string }>(
                    'print',
                    { version_ref: a.version_ref, subject_ref: a.subject_public },
                    {},
                    'operations'
                  )
                    .then(setPrint)
                    .catch((e) => setMessage(e.message))
                }
              >
                Print QR
              </button>
            )}
            {JSON.parse(a.actions_json).includes('report') && (
              <button
                className="pc-button"
                onClick={() =>
                  void ecosystemRequest<{
                    qualified_visits: number;
                    participants: number;
                    as_of: string;
                  }>(
                    'report',
                    { version_ref: a.version_ref, subject_ref: a.subject_public },
                    {},
                    'operations'
                  )
                    .then((r) =>
                      setMessage(
                        `${a.title}: ${r.qualified_visits} distinct qualifying visits from ${r.participants} enrolled collectors. Updated ${r.as_of}.`
                      )
                    )
                    .catch((e) => setMessage(e.message))
                }
              >
                View scoped report
              </button>
            )}
            {a.sessions.map((s) => (
              <div key={s.public_id}>
                <strong>
                  {s.title} · {s.status}
                </strong>
                <p>
                  {s.starts_at}–{s.ends_at} UTC · {s.timezone}
                </p>
                {JSON.parse(a.actions_json).includes('session') &&
                  ['open', 'paused', 'closed'].map((status) => (
                    <button
                      className="pc-button"
                      key={status}
                      onClick={() =>
                        void ecosystemRequest(
                          'session',
                          { session_ref: s.public_id, status },
                          {},
                          'operations'
                        )
                          .then(() =>
                            ecosystemRequest<Assignment[]>('assigned', undefined, {}, 'operations')
                          )
                          .then(setAssignments)
                          .catch((e) => setMessage(e.message))
                      }
                    >
                      {status}
                    </button>
                  ))}
              </div>
            ))}
          </article>
        ))}
      </div>
      {assignments.length === 0 ? (
        <p>No current operating assignments.</p>
      ) : (
        <section>
          <button
            className="pc-button"
            disabled={busy}
            onClick={() => {
              if (hasNativeCapability('qr_scan')) openNativeScanner('auto');
              else setCamera(!camera);
              setMessage('');
            }}
          >
            {camera ? 'Stop camera' : 'Scan collector request'}
          </button>
          {camera && (
            <video ref={video} playsInline muted style={{ width: '100%', maxWidth: 480 }} />
          )}
          {challenge && (
            <section className="pe-challenge">
              <h2>
                {challenge.startsWith('bilt:redemption:')
                  ? 'Confirm benefit delivery'
                  : 'Confirm the requested visit'}
              </h2>
              {bound ? (
                <dl>
                  <dt>Collector</dt>
                  <dd>{bound.collector}</dd>
                  <dt>{bound.benefit ? 'Benefit' : 'Subject'}</dt>
                  <dd>{bound.benefit || bound.subject}</dd>
                  <dt>Request</dt>
                  <dd>
                    {bound.evidence?.replaceAll('_', ' ') || 'Benefit delivery'} · {bound.state}
                  </dd>
                </dl>
              ) : (
                <p role="status">Checking the bound request…</p>
              )}
              <label>
                What did you confirm?
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                />
              </label>
              <button
                className="pc-button"
                disabled={
                  busy ||
                  !bound ||
                  bound.state !== 'waiting' ||
                  (!challenge.startsWith('bilt:redemption:') && reason.length < 5)
                }
                onClick={() => void confirm()}
              >
                {busy ? 'Confirming…' : 'Confirm once'}
              </button>
              <button className="pc-link" onClick={() => setChallenge('')}>
                Cancel
              </button>
            </section>
          )}
        </section>
      )}
      {message && <p role="status">{message}</p>}
      {print && (
        <section className="pe-print">
          <h2>{print.label}</h2>
          <div className="pe-qr">
            <QRCodeSVG
              size={240}
              marginSize={4}
              value={new URL(print.url, window.location.origin).href}
            />
          </div>
          <p>
            View program and earning requirements. This printed QR does not confirm a visit or
            redeem a benefit.
          </p>
          <button className="pc-button" onClick={() => window.print()}>
            Print
          </button>
        </section>
      )}
    </main>
  );
}
